import { FC, useEffect, useState } from "react";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiMultiSelect,
} from "../../../../../components";

import { useSiteCore } from "../../../../../hooks/useSiteCore";

import { PAGE_IDS } from "../../../../../helpers/constants";
import { isNotEmpty, replaceHashWithValue } from "../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import {
  ProgramTemplate,
  ProgramTemplateFormKey,
} from "../../../modelsAndSchemas/Models";
import { clientSpecificContentKeys } from "../../../siteCore/SiteCoreKeys";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";

interface ViewProgramTemplatesProps {
  programTemplates: ProgramTemplate[];
}
const pageId = PAGE_IDS.VIEW_CLIENT_SPECIFIC_CONTENT_PROGRAM_TEMPLATE;

export const ViewProgramTemplates: FC<ViewProgramTemplatesProps> = ({
  programTemplates,
}) => {
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const [resourceAccordionStatus, setResourceAccordionStatus] = useState<
    boolean[]
  >([]);
  const { isMobile } = useMediaQueries();
  const { commonLabels, fetchLabel } = useSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const {
    clientSpecificContentTitle,
    programTemplate,
    programClass,
    solutionOfferings,
    country,
    programTemplateHeading,
  } = clientSpecificContentKeys;

  const getLabel = (key: string) => {
    return fetchLabel(clientSpecificContentTitle, key);
  };

  const getCountries = (index: number) => {
    return programTemplates[index].countries.map((country) => {
      return { label: country.countryName, value: country.countryId };
    });
  };

  const handleResourceAccordionStatus = (index: number, expanded: boolean) => {
    const status = [...resourceAccordionStatus];
    status[index] = expanded;
    setResourceAccordionStatus(status);
  };

  useEffect(() => {
    let status = [...resourceAccordionStatus];
    programTemplates.forEach((_field, index) => {
      status[index] = true;
    });
    setResourceAccordionStatus(status);
  }, [programTemplates.length]);

  const renderHeader = (index: number) => {
    const label = replaceHashWithValue(
      getLabel(programTemplateHeading),
      `${index + 1}`,
    );
    return <AccordionHeaderText label={label} />;
  };
  return (
    <MuiBox data-testid="view-program-templates-tab">
      {programTemplates.map((field, index) => (
        <AccordionHeader
          key={field.programClassName}
          title={renderHeader(index)}
          className="accordion-resource-header"
          onChange={(e, expanded) =>
            handleResourceAccordionStatus(index, expanded)
          }
          isExpanded={resourceAccordionStatus[index]}
        >
          <MuiGrid
            container
            columnSpacing={2}
            rowSpacing={isMobile ? 2 : 0}
            className="flex-row"
          >
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getLabel(programTemplate)}
                value={field.programTemplateName}
              />
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getLabel(solutionOfferings)}
                value={field.solutionOfferingsName}
              />
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getLabel(programClass)}
                value={field.programClassName}
              />
            </MuiGrid>

            {isNotEmpty(getCountries(index)) ? (
              <MuiGrid size={gridSize}>
                <MuiMultiSelect
                  id={`${pageId}-${ProgramTemplateFormKey.countries}`}
                  label={getLabel(country)}
                  options={getCountries(index)}
                  showChip={true}
                  showDropdown={false}
                  className="view-country-dropdown"
                  isChipEditable={false}
                  dialogTitle={commonLabels.selectedCountries}
                  dialogWidth={376}
                  selectedValues={field.countries.map((item) => item.countryId)}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </MuiGrid>
            ) : null}
          </MuiGrid>
        </AccordionHeader>
      ))}
    </MuiBox>
  );
};
